import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Activity, Ticket, UtensilsCrossed, Sparkles, ArrowRight, Search, Train as TrainIcon, MapPin, Shield, Zap, Heart, ArrowLeftRight, IndianRupee, Clock } from 'lucide-react';
import BannerCarousel from '../components/home/BannerCarousel';
import { StoryCard } from './Stories';
import { POPULAR_ROUTES } from '../mock/mock';
import { Input } from '../components/ui/input';
import { useSettings } from '../context/SettingsContext';
import { usePageMeta } from '../hooks/usePageMeta';

import { API } from '../config';

const ALL_SERVICES = [
  {
    key: 'live_status', to: '/live-train-status', label: 'Live Status', desc: 'Track any train in real-time',
    bg: 'from-[#1E1B4B]/90 via-[#312E81]/50 to-[#0F172A]/85', shadow: 'shadow-tp-violet', icon: Activity,
    image: '/live-card-bg.jpg',
  },
  {
    key: 'pnr_status', to: '/pnr-status', label: 'PNR Status', desc: 'Coach & berth in 1 tap',
    bg: 'from-[#451A03]/90 via-[#78350F]/50 to-[#18181B]/85', shadow: 'shadow-tp-coral', icon: Ticket, 
    image: '/pnr-card-bg.jpg',
  },
  {
    key: 'food_on_train', to: '/food-on-train', label: 'Food on Train', desc: 'Hot meals at your seat',
    bg: 'from-[#14532D]/90 via-[#15803D]/50 to-[#0F172A]/85', shadow: 'shadow-tp-lime', icon: UtensilsCrossed,
    image: '/food-card-bg.jpg',
  },
  {
    key: 'stories', to: '/stories', label: 'Travel Stories', desc: 'Rail journeys & guides',
    bg: 'from-[#831843]/90 via-[#BE185D]/50 to-[#0F172A]/85', shadow: 'shadow-tp-pink', icon: Sparkles,
    image: '/stories-card-bg.jpg',
  },
];

const HOME_FAQS = [
  {
    q: 'How can I check the live running status of my train on TrainPanda?',
    a: 'Simply enter your 5-digit train number or train name in our Live Status search bar. TrainPanda provides real-time GPS coordinates, delay updates, upcoming intermediate halts, and expected platform arrival details.',
  },
  {
    q: 'How accurate is the PNR status and confirmation prediction?',
    a: 'TrainPanda pulls live booking data directly from Indian Railways reservation systems. Our intelligent confirmation engine assesses historical seat patterns to give you reliable probabilities for RAC and Waitlist tickets.',
  },
  {
    q: 'How does Food on Train delivery work at railway stations?',
    a: 'Enter your train number, PNR, or upcoming station to browse restaurant menus. Choose your favorite breakfast, thali, biryani, or snacks, place your order, and our delivery partner brings hot, sealed food straight to your coach and berth upon arrival.',
  },
  {
    q: 'How do I book a cab for railway station pickup or drop?',
    a: 'Navigate to the Cabs tab, enter your pickup and destination location, select your travel time and desired car category (Sedan, Hatchback, SUV), and book with transparent fares and trusted drivers.',
  },
  {
    q: 'Can I track trains and check PNR status without logging in?',
    a: 'Yes! TrainPanda allows you to check live train running status, train schedules, and PNR status completely free with no mandatory login required.',
  },
];

const Home = () => {
  usePageMeta(
    'TrainPanda - Check PNR - Live Train Status - Order Food in Train',
    'TrainPanda helps you track live train status, check PNR status, and order delicious food directly to your seat for a smooth and stress-free journey.'
  );
  const [stories, setStories] = useState([]);
  const [storiesLoading, setStoriesLoading] = useState(true);
  const [searchTab, setSearchTab] = useState('live');
  const [trainNo, setTrainNo] = useState('');
  const [pnr, setPnr] = useState('');
  const [openFaq, setOpenFaq] = useState(0);
  const nav = useNavigate();
  const settings = useSettings();
  const mods = settings?.modules || {};

  // Only show service cards for enabled modules
  const SERVICES = ALL_SERVICES.filter(s => mods[s.key] !== false);
  // Search tabs (only for enabled modules)
  const searchTabs = [
    { id: 'live', label: 'Live Status', icon: Activity, key: 'live_status' },
    { id: 'pnr', label: 'PNR Status', icon: Ticket, key: 'pnr_status' },
    { id: 'food', label: 'Food on Train', icon: UtensilsCrossed, key: 'food_on_train' },
  ].filter(t => mods[t.key] !== false);

  useEffect(() => {
    // ensure the active search tab is still visible after settings load
    if (searchTabs.length && !searchTabs.find(t => t.id === searchTab)) {
      setSearchTab(searchTabs[0].id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settings?.modules]);

  useEffect(() => {
    if (mods.stories === false) {
      setStoriesLoading(false);
      return;
    }
    setStoriesLoading(true);
    axios.get(`${API}/stories?limit=6`)
      .then(r => setStories(r.data?.stories || []))
      .catch(() => {})
      .finally(() => setStoriesLoading(false));
  }, [mods.stories]);

  const onSearch = () => {
    if (searchTab === 'live') nav(trainNo ? `/live-train-status?train=${trainNo}` : '/live-train-status');
    else if (searchTab === 'pnr') nav(pnr.length === 10 ? `/pnr-status?pnr=${pnr}` : '/pnr-status');
    else nav('/food-on-train');
  };

  return (
    <div className="min-h-screen pb-12">
      {/* Hero Header with H1 & Banner Carousel */}
      <section className="max-w-7xl mx-auto px-3 md:px-4 pt-4 md:pt-6">
        <h1 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-extrabold text-[#0B2E5C] tracking-tight mb-3 md:mb-4">
          Best Platform for Live Train Status, PNR, Food &amp; Cab Booking
        </h1>
        <BannerCarousel />
      </section>

      {/* 4 vibrant service cards */}
      <section className="max-w-7xl mx-auto px-3 md:px-4 mt-5 md:mt-8" data-testid="services-grid">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-5">
          {SERVICES.map((s, i) => (
            <Link
              key={s.to}
              to={s.to}
              data-testid={`service-${s.to.replace('/', '')}`}
              className="relative overflow-hidden rounded-3xl border border-white/40 p-5 md:p-6 group hover:-translate-y-1.5 transition-all duration-300 animate-fade-up shadow-lg hover:shadow-2xl flex flex-col justify-between"
              style={{ animationDelay: `${i * 80}ms`, minHeight: '190px' }}
            >
              {/* Photo Background */}
              {s.image && (
                <img
                  src={s.image}
                  alt={s.label}
                  className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-700 ease-out"
                />
              )}
              {/* Rich Gradient Scrim for high-contrast readable text */}
              <div className={`absolute inset-0 bg-gradient-to-t ${s.bg || 'from-black/85 via-black/50 to-black/30'}`} />
              <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-transparent to-black/30" />

              <div className="relative z-10">
                <div className="w-11 h-11 rounded-2xl flex items-center justify-center mb-3 bg-white/20 backdrop-blur-md border border-white/30 text-white shadow-sm">
                  <s.icon size={22} className="text-white" />
                </div>
                <div className="text-lg md:text-xl font-extrabold text-white drop-shadow-md">{s.label}</div>
                <div className="text-xs md:text-sm mt-1 text-white/90 font-medium drop-shadow">{s.desc}</div>
              </div>

              <div className="relative z-10 mt-4 inline-flex items-center gap-1.5 text-xs font-bold text-white group-hover:gap-2.5 transition-all">
                <span className="bg-white/25 backdrop-blur-sm px-3 py-1 rounded-full border border-white/30 flex items-center gap-1 group-hover:bg-white/35 transition-colors">
                  Explore <ArrowRight size={13} />
                </span>
              </div>

              <div className="absolute bottom-2 right-2 text-4xl opacity-35 group-hover:opacity-60 group-hover:scale-110 transition-all duration-500 pointer-events-none drop-shadow">
                {s.emoji}
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* Compact search */}
      {searchTabs.length > 0 && (
      <section className="max-w-3xl mx-auto px-3 md:px-4 mt-6 md:mt-8" data-testid="quick-search">
        <div className="bg-white/95 backdrop-blur-md rounded-3xl card-shadow border border-tp-violet/20 overflow-hidden">
          <div className="flex border-b border-gray-100">
            {searchTabs.map(t => (
              <button
                key={t.id}
                onClick={() => setSearchTab(t.id)}
                data-testid={`searchtab-${t.id}`}
                className={`flex-1 py-3 px-2 text-xs md:text-sm font-bold flex items-center justify-center gap-1.5 transition-all ${
                  searchTab === t.id
                    ? 'text-tp-violet border-b-2 border-tp-violet bg-tp-violet-soft/40'
                    : 'text-gray-500 hover:text-tp-ink'
                }`}
              >
                <t.icon size={14} /> <span className="hidden sm:inline">{t.label}</span><span className="sm:hidden">{t.label.split(' ')[0]}</span>
              </button>
            ))}
          </div>
          <div className="p-4 md:p-5">
            {searchTab === 'live' && (
              <div className="flex items-center gap-2 border-2 border-gray-100 rounded-2xl focus-within:border-tp-violet px-3 transition-colors">
                <TrainIcon size={18} className="text-tp-violet" />
                <Input value={trainNo} onChange={e => setTrainNo(e.target.value)} placeholder="Train number or name e.g. 12951" className="border-0 focus-visible:ring-0 h-11 text-base" data-testid="input-train" />
              </div>
            )}
            {searchTab === 'pnr' && (
              <div className="flex items-center gap-2 border-2 border-gray-100 rounded-2xl focus-within:border-tp-violet px-3 transition-colors">
                <Ticket size={18} className="text-tp-violet" />
                <Input value={pnr} onChange={e => setPnr(e.target.value.replace(/\D/g, '').slice(0, 10))} placeholder="10-digit PNR" className="border-0 focus-visible:ring-0 h-11 text-base tracking-widest" data-testid="input-pnr" />
              </div>
            )}
            {searchTab === 'food' && (
              <div className="text-sm text-gray-600 bg-tp-lime-soft border border-tp-lime/30 rounded-2xl px-4 py-3 flex items-center gap-2">
                <MapPin size={16} className="text-tp-lime-deep" />
                Browse menus &amp; order — delivered to your seat. <Link to="/food-on-train" className="text-tp-lime-deep font-bold ml-auto">Open →</Link>
              </div>
            )}
            <button
              onClick={onSearch}
              data-testid="search-btn"
              className="w-full mt-3 btn-primary rounded-2xl h-12 text-sm md:text-base flex items-center justify-center gap-2"
            >
              <Search size={16} /> {searchTab === 'food' ? 'Open Food' : 'Search Now'}
            </button>
          </div>
        </div>
      </section>
      )}

      {/* Train Tools — Between Stations, Fare, Time Table */}
      <section className="max-w-7xl mx-auto px-3 md:px-4 mt-8" data-testid="train-tools-section">
        <div className="flex items-end justify-between mb-4">
          <h2 className="text-2xl md:text-3xl font-extrabold text-tp-ink">Train Tools</h2>
          <Link to="/trains" className="text-sm text-tp-violet font-bold hover:underline">Open all →</Link>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 md:gap-4">
          {[
            { to: '/trains?tab=between', label: 'Trains Between Stations', desc: 'All trains, timings & running days', icon: ArrowLeftRight },
            { to: '/trains?tab=fare', label: 'Fare Calculator', desc: 'Class-wise fares, adult & child', icon: IndianRupee },
            { to: '/trains?tab=timetable', label: 'Train Time Table', desc: 'Halts, platforms & distance', icon: Clock },
          ].map((c) => (
            <Link
              key={c.to}
              to={c.to}
              data-testid={`traintool-${c.to.split('=')[1]}`}
              className="bg-white/95 backdrop-blur-md rounded-2xl p-4 md:p-5 border border-tp-violet/15 card-shadow hover:border-tp-violet hover:-translate-y-0.5 transition-all group flex items-center gap-4"
            >
              <div className="w-12 h-12 rounded-2xl bg-tp-violet-soft text-tp-violet flex items-center justify-center shrink-0 group-hover:bg-tp-violet group-hover:text-white transition-colors">
                <c.icon size={22} />
              </div>
              <div className="min-w-0">
                <div className="font-extrabold text-tp-ink text-sm md:text-base">{c.label}</div>
                <div className="text-xs text-tp-ink-muted mt-0.5">{c.desc}</div>
              </div>
              <ArrowRight size={16} className="text-gray-300 group-hover:text-tp-violet ml-auto shrink-0 group-hover:translate-x-0.5 transition-all" />
            </Link>
          ))}
        </div>
      </section>

      {/* Trust strip */}
      <section className="max-w-7xl mx-auto px-3 md:px-4 mt-8 grid grid-cols-3 gap-2 md:gap-4">
        {[
          { icon: Zap, label: 'Real-time', sub: '95%+ accurate' },
          { icon: Shield, label: 'No login', sub: 'Quick search' },
          { icon: Heart, label: 'Loved by', sub: '10K+ travellers' },
        ].map((t, i) => (
          <div key={t.label} className="bg-white/95 backdrop-blur-md rounded-2xl p-3 md:p-4 border border-tp-violet/15 card-shadow flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-tp-violet-soft text-tp-violet flex items-center justify-center"><t.icon size={16} /></div>
            <div className="leading-tight">
              <div className="text-sm font-bold text-tp-ink">{t.label}</div>
              <div className="text-[11px] text-tp-ink-muted">{t.sub}</div>
            </div>
          </div>
        ))}
      </section>

      {/* Travel Stories rail */}
      {mods.stories !== false && (
        <section className="max-w-7xl mx-auto px-3 md:px-4 mt-10" data-testid="home-stories-rail">
          <div className="flex items-end justify-between mb-4">
            <div>
              <h2 className="text-2xl md:text-3xl font-extrabold text-tp-ink flex items-center gap-2">
                <span className="bg-gradient-pink bg-clip-text text-transparent">Travel Stories</span>
                <Sparkles className="text-tp-pink" size={22} />
              </h2>
              <p className="text-sm text-tp-ink-muted">Railway journeys, food guides &amp; destination tips</p>
            </div>
            <Link to="/stories" className="text-sm text-tp-violet font-bold hover:underline">View all →</Link>
          </div>
          {storiesLoading ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {[1, 2, 3].map((n) => (
                <div key={n} className="bg-white rounded-3xl border border-gray-100 card-shadow overflow-hidden flex flex-col p-0">
                  <div className="aspect-[16/10] w-full shimmer-wave" />
                  <div className="p-6 space-y-3 flex-1 flex flex-col justify-between">
                    <div className="space-y-2.5">
                      <div className="h-5 w-24 rounded-full shimmer-wave-purple" />
                      <div className="h-6 rounded-xl w-5/6 shimmer-wave" />
                      <div className="h-4 rounded-lg w-full shimmer-wave" />
                    </div>
                    <div className="pt-3 border-t border-gray-100 flex items-center justify-between">
                      <div className="h-4 w-24 rounded-md shimmer-wave" />
                      <div className="h-4 w-12 rounded-md shimmer-wave" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : stories.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {stories.slice(0, 3).map(s => <StoryCard key={s.id} s={s} />)}
            </div>
          ) : null}
        </section>
      )}

      {/* Popular routes */}
      <section className="max-w-7xl mx-auto px-3 md:px-4 mt-10">
        <h2 className="text-2xl md:text-3xl font-extrabold text-tp-ink mb-4">Popular routes</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-3">
          {POPULAR_ROUTES.slice(0, 8).map(r => (
            <Link
              key={r.from + r.to}
              to="/live-train-status"
              className="bg-white/95 backdrop-blur-md rounded-2xl p-3 md:p-4 border border-tp-violet/15 card-shadow hover:border-tp-violet hover:-translate-y-0.5 transition-all"
            >
              <div className="text-xs font-bold text-tp-violet">{r.from} → {r.to}</div>
              <div className="text-[11px] text-tp-ink-muted mt-1">{r.trains || 'Multiple trains'}</div>
            </Link>
          ))}
        </div>
      </section>

      {/* Comprehensive Travel Platform Overview (300+ Words) & FAQs */}
      <section className="max-w-7xl mx-auto px-3 md:px-4 mt-12 mb-6">
        <div className="bg-gradient-to-br from-white via-[#FAF7F2] to-white rounded-3xl p-6 md:p-10 border border-gray-100 card-shadow space-y-6">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-tp-orange/10 text-tp-orange text-xs font-bold uppercase tracking-wider mb-2">
              <Sparkles size={13} /> India's Trusted Railway Companion
            </div>
            <h2 className="text-2xl md:text-3xl font-extrabold text-[#0B2E5C] tracking-tight">
              Best Platform for Live Train Status, PNR, Food &amp; Cab Booking
            </h2>
          </div>

          <div className="prose prose-sm md:prose-base text-gray-600 leading-relaxed space-y-4">
            <p>
              Make your train journey easier and more convenient with TrainPanda, your one-stop platform for live train status, PNR status checks, food delivery on trains, and railway station cab booking. Whether you are planning a trip or already travelling, TrainPanda helps you access essential railway travel services in one place.
            </p>
            <h4 className="text-lg md:text-xl font-bold text-[#0B2E5C] pt-2">
              Check PNR Status Online &amp; Track Ticket Confirmation
            </h4>
            <p>
              Wondering whether your train ticket is confirmed? TrainPanda makes it easy to check PNR status online and stay updated about your reservation. Check your railway ticket status, understand whether your booking is Confirmed, RAC, or Waiting List, and review the latest available reservation information. If you are looking for the best app to check PNR status, an IRCTC PNR check, or the top PNR status websites for Indian Railways, TrainPanda offers a convenient way to get started.
            </p>
            <h4 className="text-lg md:text-xl font-bold text-[#0B2E5C] pt-2">
              Live Train Running Status &amp; Real-Time Tracking
            </h4>
            <p>
              Stay informed throughout your journey with Live Train Running Status. Track your train's current running information, expected arrival and departure details, and delay updates. Whether you search for Indian Railway train status, IRCTC train status, train status by number, or a reliable alternative for train tracking, TrainPanda helps you plan your journey with greater confidence.
            </p>
            <h4 className="text-lg md:text-xl font-bold text-[#0B2E5C] pt-2">
              Order Food in Train – Fresh Meals Delivered to Seat
            </h4>
            <p>
              Enjoy convenient food delivery on trains without relying only on station snacks or carrying meals from home. Explore train food ordering options and arrange meal delivery to your seat through available restaurant partners. TrainPanda helps make railway food booking simple.
            </p>
            <h4 className="text-lg md:text-xl font-bold text-[#0B2E5C] pt-2">
              Book a Cab to Your Railway Station
            </h4>
            <p>
              Complete your travel plans with convenient Railway Station Pick Drop Cab Booking. Arrange station pickup and drop services to help you travel between your home, hotel, or other destination and the railway station. Find a practical railway station transfer taxi option for smoother last-mile connectivity.
            </p>
            <h3 className="text-xl md:text-2xl font-bold text-[#0B2E5C] pt-4">
              Travel Smarter with TrainPanda
            </h3>
            <p>
              From checking PNR status and tracking trains live to ordering food and booking cabs, TrainPanda brings essential travel services together. Visit TrainPanda.com and make your next train journey more comfortable, convenient, and stress-free.
            </p>
          </div>

          {/* Frequently Asked Questions (FAQ) */}
          <div className="pt-8 border-t border-gray-200/80">
            <div className="flex items-center gap-2 mb-6">
              <div className="w-8 h-8 rounded-xl bg-[#0B2E5C] text-white flex items-center justify-center font-bold text-sm">
                ?
              </div>
              <h3 className="text-xl md:text-2xl font-bold text-[#0B2E5C]">
                Frequently Asked Questions (FAQ)
              </h3>
            </div>

            <div className="space-y-3">
              {HOME_FAQS.map((faq, idx) => (
                <div
                  key={idx}
                  className="border border-gray-200/70 rounded-2xl overflow-hidden bg-white/80 transition-colors"
                >
                  <button
                    type="button"
                    onClick={() => setOpenFaq(openFaq === idx ? null : idx)}
                    className="w-full px-5 py-4 text-left font-bold text-gray-800 flex items-center justify-between gap-4 hover:text-tp-orange transition-colors"
                  >
                    <span>{faq.q}</span>
                    <span className="text-xl text-gray-400 font-light select-none">
                      {openFaq === idx ? '−' : '+'}
                    </span>
                  </button>
                  {openFaq === idx && (
                    <div className="px-5 pb-4 text-sm text-gray-600 leading-relaxed border-t border-gray-100/80 pt-3">
                      {faq.a}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
